import React, { useCallback, useState, useEffect } from "react";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { useIntl } from "react-intl";

import colors from "../../../colors";
import ListFooter from "./listFooter";
import Tracking from "../../tracking2";
import {
  integerSorter,
  invalidForSelect,
} from "../../../modules/consignmentFunctions";
import LoadTable from "../../app/generic/loadTable";
import StateIcon from "../../app/generic/stateIcon";
import CustomIcon from "../../app/generic/customIcon";
import ConsignmentRowMenu from "./consignmentRowMenu";
import ConsignmentStateIcon from "./consignmentStateIcon";
import ConsignmentDateTime from "../../app/generic/consignmentDateTime";
import TableRowActionsButton from "../../app/generic/tableRowActionsButton";
import CalculationTitle from "../../calculator/resultDisplay/calculationTitle";

function ConsignmentListTable({
  push,
  sentList,
  language,
  bulkDelete,
  consignments,
  trackingList,
  loadConsignments,
  selectedConsignmentIds,
  setSelectedConsignmentIds,
}) {
  const intl = useIntl();
  const handleDetailEditClick = useCallback(
    (consignmentId) => {
      push(`/consignment/${consignmentId}/${sentList ? "detail" : "edit"}`);
    },
    [push, sentList]
  );
  const handleDoubleClick = useCallback(
    (c) => handleDetailEditClick(c.key),
    [handleDetailEditClick]
  );

  const [tableProps, setTableProps] = useState();
  useEffect(() => {
    trackingList
      ? setTableProps({
          expandIcon: (props) => (
            <CustomIcon
              type={props.expanded ? "minus" : "search"}
              title={
                props.expanded
                  ? intl.formatMessage({
                      id: "consignment.list.hideHistory",
                      defaultMessage: "Skrýt detailní historii",
                    })
                  : intl.formatMessage({
                      id: "consignment.list.showHistory",
                      defaultMessage: "Prohlédnout detailní historii zásilky",
                    })
              }
              onClick={(e) => {
                props.onExpand(props.record, e);
              }}
            />
          ),
          expandedRowRender: (consignment) => (
            <Tracking consignmentCode={consignment.code} />
          ),
        })
      : !sentList &&
        setTableProps({
          footer: () => <ListFooter consignments={consignments} />,
        });
  }, [intl, sentList, trackingList, consignments, selectedConsignmentIds]);

  const columns = [
    ...(!sentList
      ? [
          {
            key: "valid",
            title: intl.formatMessage({
              id: "consignment.state",
              defaultMessage: "Stav",
            }),
            dataIndex: "valid",
            width: language && language.iso6391Code === "en" ? 65 : 60,
            className: "center",
            render: (valid, consignment) =>
              consignment.state === "CP" ? (
                <CustomIcon
                  type="form"
                  color={colors.YELLOW}
                  title={intl.formatMessage({
                    id: "consignment.list.copyTitle",
                    defaultMessage:
                      "Zásilka je kopií jiné, zásilku bude možné odeslat do DHL až po její kontrole/editaci a uložení!",
                  })}
                />
              ) : (
                <StateIcon
                  valid={valid}
                  messages={[
                    valid
                      ? intl.formatMessage({
                          id: "consignment.list.valid",
                          defaultMessage: "Zásilka je validní!",
                        })
                      : intl.formatMessage({
                          id: "consignment.list.invalid",
                          defaultMessage:
                            "Zásilka obsahuje chybné nebo nekompletní údaje!",
                        }),
                  ]}
                />
              ),
          },
        ]
      : []),
    ...(sentList
      ? [
          {
            key: "codes",
            title: intl.formatMessage({
              id: "consignment.code",
              defaultMessage: "Číslo zásilky",
            }),
            dataIndex: "codes",
            width: 130,
            sorter: (a, b) => (a.codes ? a.codes.localeCompare(b.codes) : -1),
          },
        ]
      : []),
    {
      key: "insertionDateTime",
      title: intl.formatMessage({
        id: "consignment.insertionDateTime",
        defaultMessage: "Datum založení",
      }),
      dataIndex: "insertionDateTime",
      width: 160,
      render: (insertionDateTime, consignment) => (
        <ConsignmentDateTime
          intl={intl}
          dateTime={insertionDateTime}
          intlIdFreApi="consignment.freapi"
          intlId="consignment.createdByDhl"
          operation={consignment.createdByDhl}
          freApi={consignment.freApiConsignment}
          defaultMessage="Zásilka byla vytvořena zaměstnancem DHL."
          defaultMessageFreApi="Zásilka byla odeslána do DHL skrze FREAPI."
        />
      ),
      sorter: (a, b) => a.insertionDateTime.localeCompare(b.insertionDateTime),
    },
    {
      key: "customerReference",
      title: intl.formatMessage({
        id: "consignment.customerReference",
        defaultMessage: "Reference",
      }),
      dataIndex: "customerReference",
      sorter: (a, b) =>
        a.customerReference
          ? a.customerReference.localeCompare(b.customerReference)
          : -1,
    },
    {
      key: "pickup",
      title: intl.formatMessage({
        id: "consignment.pickup",
        defaultMessage: "Nakládka",
      }),
      dataIndex: "pickup",
      sorter: (a, b) => (a.pickup ? a.pickup.localeCompare(b.pickup) : -1),
    },
    {
      key: "delivery",
      title: intl.formatMessage({
        id: "consignment.delivery",
        defaultMessage: "Vykládka",
      }),
      dataIndex: "delivery",
      sorter: (a, b) =>
        a.delivery ? a.delivery.localeCompare(b.delivery) : -1,
    },
    {
      key: "countOfCollis",
      title: intl.formatMessage({
        id: "consignment.countOfCollis",
        defaultMessage: "Počet kusů",
      }),
      dataIndex: "countOfCollis",
      className: "center-now",
      width: language && language.iso6391Code === "en" ? 85 : 70,
    },
    {
      key: "type",
      title: intl.formatMessage({
        id: "consignment.type",
        defaultMessage: "Typ",
      }),
      dataIndex: "type",
      width: 85,
      sorter: (a, b) => (a.type ? a.type.localeCompare(b.type) : -1),
    },
    {
      key: "cashOnDelivery",
      title: intl.formatMessage({
        id: "consignment.cashOnDelivery",
        defaultMessage: "Dobírka",
      }),
      dataIndex: "cashOnDelivery",
      width: 130,
    },
    {
      key: "insurance",
      title: intl.formatMessage({
        id: "consignment.insurance",
        defaultMessage: "Připojištění",
      }),
      dataIndex: "insurance",
      width: 105,
      className: "center",
      render: (insurance, consignment) => (
        <CustomIcon
          type={insurance ? "check" : "close"}
          title={
            insurance
              ? consignment.consignmentPrice
                ? intl.formatMessage(
                    {
                      id: "consignment.insurance.withPrice",
                      defaultMessage:
                        "Zásilka má aktivní připojištění na hodnotu {price} {currency}, poplatek za pojištění je {insurancePrice} {insuranceCurrency}.",
                    },
                    {
                      price: consignment.consignmentPrice,
                      currency: consignment.consignmentPriceCurrency,
                      insurancePrice:
                        consignment.insuranceDetail &&
                        consignment.insuranceDetail.price &&
                        consignment.insuranceDetail.price.toFixed(2),
                      insuranceCurrency:
                        consignment.insuranceDetail &&
                        consignment.insuranceDetail.currency,
                    }
                  )
                : intl.formatMessage({
                    id: "consignment.insurance.noPrice",
                    defaultMessage:
                      "Zásilka má aktivní připojištění, ale nemá zadanou hodnotu!",
                  })
              : intl.formatMessage({
                  id: "consignment.insurance.noInsurance",
                  defaultMessage: "Zásilka nemá připojištění!",
                })
          }
        />
      ),
    },
    ...(trackingList
      ? [
          {
            key: "state",
            title: intl.formatMessage({
              id: "consignment.status",
              defaultMessage: "Status",
            }),
            dataIndex: "state",
            width: 75,
            render: (state, c) => (
              <ConsignmentStateIcon
                consignmentState={state}
                proofOfDeliveryUrl={c.proofOfDeliveryUrl}
              />
            ),
          },
          {
            key: "lastTrackMessage",
            title: intl.formatMessage({
              id: "consignment.lastTrackMessage",
              defaultMessage: "Stav zásilky",
            }),
            dataIndex: "lastTrackMessage",
          },
        ]
      : sentList
      ? [
          {
            key: "sentDateTime",
            title: intl.formatMessage({
              id: "consignment.sentDateTime",
              defaultMessage: "Odeslání dat",
            }),
            dataIndex: "sentDateTime",
            width: 150,
            sorter: (a, b) =>
              a.sentDateTime
                ? a.sentDateTime.localeCompare(b.sentDateTime)
                : -1,
            render: (sentDateTime, consignment) => (
              <ConsignmentDateTime
                intl={intl}
                dateTime={sentDateTime}
                intlId="consignment.sentByDhl"
                intlIdFreApi="consignment.freapi"
                intlIdCancel="consignment.state.cancelled"
                cancelled={consignment.cancelled}
                operation={consignment.sentByDhl}
                freApi={consignment.freApiConsignment}
                cancelledUser={consignment.cancellerUser}
                defaultMessageCancel="Zásilka byla stornována objednatelem."
                defaultMessage="Zásilka byla odeslána do DHL zaměstnancem DHL."
                defaultMessageFreApi="Zásilka byla odeslána do DHL skrze FREAPI."
              />
            ),
          },
        ]
      : []),
    ...(!trackingList
      ? [
          {
            key: "actions",
            title: "",
            dataIndex: "",
            render: (c) => (
              <TableRowActionsButton
                menu={
                  <ConsignmentRowMenu
                    consignment={c}
                    sentList={sentList}
                    consignmentId={c.key}
                    disabled={bulkDelete}
                    onEdit={handleDetailEditClick}
                    loadConsignments={loadConsignments}
                  />
                }
              />
            ),
          },
          ...(consignments &&
          consignments !== "e" &&
          consignments.some((c) => c.transportPrice)
            ? [
                {
                  key: "transportPrice",
                  title: intl.formatMessage({
                    id: "calculator.price",
                    defaultMessage: "Cena",
                  }),
                  dataIndex: "transportPrice",
                  width: 100,
                  sorter: (a, b) =>
                    a.transportPrice && b.transportPrice
                      ? integerSorter(a.transportPrice, b.transportPrice)
                      : a.transportPrice
                      ? 1
                      : -1,
                  render: (transportPrice, consignment) => (
                    <CalculationTitle
                      price={transportPrice}
                      items={consignment.transportPriceItems}
                      priceCurrency={consignment.transportPriceCurrency}
                    />
                  ),
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <LoadTable
      what={intl.formatMessage({
        id: "consignment.genitivMultiple",
        defaultMessage: "zásilek",
      })}
      columns={columns}
      getCheckboxProps={(record) => ({
        disabled: invalidForSelect(record) && !bulkDelete,
      })}
      values={consignments}
      tableProps={tableProps}
      emptyText={intl.formatMessage({
        id: "consignment.noConsignments",
        defaultMessage: "žádné zásilky",
      })}
      onLoadValues={loadConsignments}
      onRowDoubleClick={handleDoubleClick}
      selectedRows={selectedConsignmentIds}
      setSelectedRows={setSelectedConsignmentIds}
    />
  );
}

const mapStateToProps = ({ language }) => ({
  language: language.data,
});

export default connect(mapStateToProps, { push })(ConsignmentListTable);
